import { useState } from "react";
import { toast } from "sonner";
import { Flag, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", email: "", organization: "", work: "", details: "", good_faith: false, accurate: false };

const Check = ({ checked, onChange, id, children }) => (
  <label className="flex items-start gap-2 text-xs leading-relaxed text-[#D6CCBC]">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" data-testid={id} /><span>{children}</span>
  </label>
);

export const RightsReportButton = ({ sid, title }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ready = f.name.trim() && f.email.trim() && f.work.trim() && f.details.trim().length >= 10 && f.good_faith && f.accurate;
  const send = async () => {
    setBusy(true);
    try { await api.post(`/public/stories/${sid}/rights-report`, f); toast.success("Report sent. Our team will review it."); setOpen(false); setF(EMPTY); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-[11px] text-[#7A7468] transition-colors hover:text-[#F3EBDD]" data-testid="rights-report-open"><Flag className="h-3 w-3" />Report a rights issue</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="rights-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Report a rights issue</DialogTitle>
            <DialogDescription>For rights-holders who believe “{title}” uses their work without permission. We review every report. If we remove a story from Reader Mode, the writer keeps their private copy.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input value={f.name} onChange={set("name")} placeholder="Your name" data-testid="rights-name-input" />
            <Input type="email" value={f.email} onChange={set("email")} placeholder="Email for our reply" data-testid="rights-email-input" />
            <Input value={f.organization} onChange={set("organization")} placeholder="Company or estate (optional)" className="sm:col-span-2" data-testid="rights-org-input" />
            <Input value={f.work} onChange={set("work")} placeholder="Your work, e.g. the book or series" className="sm:col-span-2" data-testid="rights-work-input" />
            <Textarea value={f.details} onChange={set("details")} rows={4} maxLength={3000} placeholder="What in this story uses your work, and what you'd like us to do" className="sm:col-span-2" data-testid="rights-details-input" />
            {f.details.trim().length > 0 && f.details.trim().length < 10 && <p className="-mt-2 text-[11px] text-[#A8A090] sm:col-span-2" data-testid="rights-details-hint">Please add a little more detail (at least 10 characters).</p>}
          </div>
          <div className="space-y-2">
            <Check checked={f.good_faith} onChange={(v) => setF({ ...f, good_faith: v })} id="rights-good-faith">I believe in good faith that this use isn't authorized by the rights-holder, its agent or the law.</Check>
            <Check checked={f.accurate} onChange={(v) => setF({ ...f, accurate: v })} id="rights-accurate">The information in this report is accurate, and I own the rights or am allowed to act for the owner.</Check>
          </div>
          <Button onClick={send} disabled={!ready || busy} data-testid="rights-report-submit">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Send report</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
